/**
 * Medical Aid Search Modal — 医療助成 補助入力
 * Matches RA2 medical search flow: filters → 適用 (validations) → interval table → row applies to form.
 */

import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@shared/providers/AuthContext';
import { usePatientMasterData } from '@/hooks/main/usePatientMasterData';
import {
  useMedicalAidPrefectures,
  useSearchMedicalAidInsurers,
  useFetchMedicalAidIntervalRows,
  type MedicalAidInsurer,
} from '@/hooks/main/useMedicalAidSearch';
import { useMainClinicInformationList } from '@/hooks/main/useMainClinicInformation';
import { AppModal, AppButton, AppSelect, AppSpin, AppTable } from '@/components/atoms';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { convGtJDate } from '@/utils/dateConverter';
import {
  formatSpecialCalculation,
  isMedicalAidNoClaim,
} from '@pages/main/patient/utils/medicalAidCalc';
import { useInsurerDialogMessages } from '@/hooks/main/useInsurerDialogMessages';
import { useTheme } from '@shared/providers/theme/ThemeContext';

export interface MedicalAidSearchResult {
  insurer_type_id: number;
  insurer_id: number;
  sub_number: number;
  medical_aid_number: string;
  cost_rate: number;
  family_cost_rate: number;
  is_special: boolean;
  is_nonoutput_rezept: boolean;
  is_partial_aid: boolean;
  application_start_date: string;
  application_end_date: string;
  effective_from: string;
  effective_to: string;
  claim_type_id?: number | null;
  is_enabled_invoice?: boolean;
  calc_type?: number | null;
  limit_amount?: number | null;
  limit_num_month?: number | null;
  limit_amount_month?: number | null;
}

const DEFAULT_PREFECTURE = '東京都';

const MERGE_WARNING_MESSAGE =
  '【公費負担者番号】合併、再編等により名称が変更になっている可能性があります。';

function buildSearchResult(row: MedicalAidInsurer, insurerTypeId: number): MedicalAidSearchResult {
  return {
    insurer_type_id: insurerTypeId,
    insurer_id: row.id,
    sub_number: row.sub_number ?? 0,
    medical_aid_number: row.insurer_number,
    cost_rate: Number(row.cost_rate ?? 0),
    family_cost_rate: Number(row.family_cost_rate ?? 0),
    is_special: Boolean(row.is_special),
    is_nonoutput_rezept: Boolean(row.is_nonoutput_rezept),
    is_partial_aid: Boolean(row.is_partial_aid),
    application_start_date: row.available_from || '',
    application_end_date: row.available_to || '',
    effective_from: row.available_from || '',
    effective_to: row.available_to || '',
    claim_type_id: row.medix_claim_type_id ?? null,
    is_enabled_invoice: row.is_enabled_invoice,
    calc_type: row.calc_type,
    limit_amount: row.limit_amount,
    limit_num_month: row.limit_num_month,
    limit_amount_month: row.limit_amount_month,
  };
}

interface MedicalAidSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (result: MedicalAidSearchResult) => void;
  initialInsurerTypeId?: number;
  initialMedicalAidNumber?: string;
  initialIntervalRows?: MedicalAidInsurer[];
}

const MedicalAidSearchModal: React.FC<MedicalAidSearchModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  initialInsurerTypeId,
  initialMedicalAidNumber,
  initialIntervalRows = [],
}) => {
  const { clinic } = useAuth();
  const { colors } = useTheme();
  const toast = useAppToast();
  const modal = useAppModal();
  const { getMedicalAidNoClaimMessage } = useInsurerDialogMessages();

  const siteType = clinic?.site_type || 'medix';
  const clinicNumber = clinic?.clinic_number || '';

  const { data: masterData } = usePatientMasterData();
  const { data: clinicInfoData } = useMainClinicInformationList(1);

  const [step, setStep] = useState<'filters' | 'intervals'>('filters');
  const [insurerTypeId, setInsurerTypeId] = useState<number | null>(null);
  const [prefectureName, setPrefectureName] = useState<string>(DEFAULT_PREFECTURE);
  const [selectedInsurerId, setSelectedInsurerId] = useState<number | null>(null);
  const [insurers, setInsurers] = useState<MedicalAidInsurer[]>([]);
  const [intervalRows, setIntervalRows] = useState<MedicalAidInsurer[]>([]);
  const activeIntervalRows = initialIntervalRows.length > 0 ? initialIntervalRows : intervalRows;
  const activeStep = initialIntervalRows.length > 0 ? 'intervals' : step;

  const { data: prefectures = [] } = useMedicalAidPrefectures(siteType, clinicNumber, isOpen);
  const {
    mutateAsync: searchMedicalAidInsurersMutateAsync,
    mutate: searchMedicalAidInsurersMutate,
    ...searchInsurersMutation
  } = useSearchMedicalAidInsurers(siteType, clinicNumber);
  const fetchIntervalsMutation = useFetchMedicalAidIntervalRows(siteType, clinicNumber);

  const [didInit, setDidInit] = useState(false);

  /** Same as main form (MedicalAidSection): master 医療助成 types + 「無し」(id=0), label 無し for id 0. */
  const medicalAidTypesList = useMemo(() => {
    // Filter out master's "None" if it exists (usually id=1) to avoid duplicates with our legacy id=0
    const filteredMaster = (masterData?.insurer_types ?? []).filter(
      t => t.id !== 1 || (t.name !== 'なし' && t.name !== '無し')
    );
    const types = [...filteredMaster];
    if (!types.some(t => t.id === 0)) {
      types.unshift({ id: 0, name: '無し', is_health_insurance: false });
    }
    return types;
  }, [masterData?.insurer_types]);

  const medicalAidTypeSelectOptions = useMemo(
    () =>
      medicalAidTypesList.map(type => ({
        value: type.id,
        label: type.id === 0 ? '無し' : type.name,
      })),
    [medicalAidTypesList]
  );

  const prefectureSelectOptions = useMemo(
    () =>
      prefectures.map(pref => ({
        value: pref.name,
        label: pref.name,
      })),
    [prefectures]
  );

  const medicalAidInsurerSelectOptions = useMemo(
    () =>
      insurers.map(insurer => {
        const calcSuffix = formatSpecialCalculation(
          insurer.calc_type,
          insurer.limit_amount,
          insurer.limit_num_month,
          insurer.limit_amount_month
        );
        const base = `${insurer.insurer_number} ${insurer.insurer_name}`;
        return {
          value: insurer.id,
          label: calcSuffix ? `${base}　${calcSuffix}` : base,
        };
      }),
    [insurers]
  );

  const resolvedDefaultPrefecture = useMemo(() => {
    let defaultPrefName = DEFAULT_PREFECTURE;

    const clinicPrefId = clinicInfoData?.data?.data?.[0]?.prefecture_id;
    if (clinicPrefId) {
      const prefMatch =
        prefectures.find(p => p.id === clinicPrefId) ||
        masterData?.prefectures?.find(p => p.id === clinicPrefId);
      if (prefMatch) {
        defaultPrefName = prefMatch.name;
      }
    }

    const names = prefectures.map(p => p.name);
    if (names.includes(defaultPrefName)) return defaultPrefName;
    return prefectures[0]?.name ?? DEFAULT_PREFECTURE;
  }, [prefectures, clinicInfoData, masterData]);

  // A2 No494: no error message on close — just close the modal
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      setStep('filters');
      setIntervalRows([]);
      setDidInit(false);
      setInsurerTypeId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || initialIntervalRows.length === 0) return;
    setIntervalRows(initialIntervalRows);
    setStep('intervals');
    setInsurerTypeId(initialIntervalRows[0]?.insurer_type_id ?? initialInsurerTypeId ?? null);
    setDidInit(true);
  }, [isOpen, initialIntervalRows, initialInsurerTypeId]);

  useEffect(() => {
    if (
      !isOpen ||
      didInit ||
      initialIntervalRows.length > 0 ||
      prefectures.length === 0 ||
      medicalAidTypesList.length === 0
    ) {
      return;
    }

    const validTypeIds = new Set(medicalAidTypesList.map(t => t.id));
    const fallbackType =
      initialInsurerTypeId != null && validTypeIds.has(initialInsurerTypeId)
        ? initialInsurerTypeId
        : medicalAidTypesList[0].id;

    const matchPrefectureByAddress = (address?: string | null) =>
      address ? prefectures.find(p => address.includes(p.name))?.name : undefined;

    const resolveFromExactNumber = async () => {
      if (!initialMedicalAidNumber) return null;
      try {
        const data = await searchMedicalAidInsurersMutateAsync({
          exact_insurer_number: initialMedicalAidNumber,
        });
        const first = data?.[0];
        if (!first) return null;
        return {
          prefectureName: matchPrefectureByAddress(first.address1),
          insurerTypeId: validTypeIds.has(first.insurer_type_id)
            ? first.insurer_type_id
            : undefined,
          intervals:
            data.length > 1
              ? [...data].sort((a, b) => (b.sub_number ?? 0) - (a.sub_number ?? 0))
              : null,
        };
      } catch (err) {
        console.error('Failed global search:', err);
        return null;
      }
    };

    resolveFromExactNumber().then(match => {
      if (match?.intervals) {
        setIntervalRows(match.intervals);
        setStep('intervals');
      }
      setPrefectureName(match?.prefectureName ?? resolvedDefaultPrefecture);
      setInsurerTypeId(match?.insurerTypeId ?? fallbackType);
      setDidInit(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    didInit,
    initialMedicalAidNumber,
    initialInsurerTypeId,
    resolvedDefaultPrefecture,
    prefectures,
    medicalAidTypesList,
    searchMedicalAidInsurersMutateAsync,
  ]);

  // Use stable `mutate` only — the full mutation object identity can change each render and
  // would retrigger this effect → infinite /insurers/search calls.
  useEffect(() => {
    if (!isOpen || !didInit || !insurerTypeId) return;
    setSelectedInsurerId(null);
    if (insurerTypeId === 0) {
      setInsurers([]);
      setSelectedInsurerId(null);
      return;
    }
    searchMedicalAidInsurersMutate(
      {
        insurer_type_id: insurerTypeId,
        prefectures_name: prefectureName || undefined,
      },
      {
        onSuccess: data => {
          setInsurers(data);
          let matchId = null;
          if (initialMedicalAidNumber) {
            const match = data.find(i => i.insurer_number === initialMedicalAidNumber);
            if (match) {
              matchId = match.id;
            }
          }
          setSelectedInsurerId(matchId ?? data[0]?.id ?? null);
        },
        onError: error => {
          console.error('Failed to load insurers:', error);
          setInsurers([]);
          setSelectedInsurerId(null);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    didInit,
    insurerTypeId,
    prefectureName,
    searchMedicalAidInsurersMutate,
    initialMedicalAidNumber,
  ]);

  const handleInsurerTypeChange = (typeId: number) => {
    setInsurerTypeId(typeId);
    setSelectedInsurerId(null);
  };

  const handlePrefectureChange = (prefName: string) => {
    setPrefectureName(prefName);
    setSelectedInsurerId(null);
  };

  const applyIntervalRow = useCallback(
    (row: MedicalAidInsurer) => {
      const selectedInsurerTypeId = insurerTypeId ?? row.insurer_type_id;
      if (!selectedInsurerTypeId) return;
      onSelect(buildSearchResult(row, selectedInsurerTypeId));
    },
    [insurerTypeId, onSelect]
  );

  const handleApplyClick = async () => {
    if (!insurerTypeId) return;
    if (insurerTypeId === 0) return;
    const selected = insurers.find(i => i.id === selectedInsurerId);
    if (!selected) {
      toast.warning('公費負担者を選択してください');
      return;
    }

    if (isMedicalAidNoClaim(selected)) {
      modal.warning({
        title: '警告',
        content: getMedicalAidNoClaimMessage().message,
        okText: 'OK',
        onOk: () => {
          onClose();
        },
      });
      return;
    }

    const fetchIntervalRows = () =>
      new Promise<MedicalAidInsurer[]>((resolve, reject) => {
        fetchIntervalsMutation.mutate(
          { insurer_type_id: insurerTypeId, insurer_number: selected.insurer_number },
          {
            onSuccess: data => resolve(data),
            onError: err => reject(err),
          }
        );
      });

    try {
      const data = await fetchIntervalRows();
      const sorted = [...data].sort((a, b) => (b.sub_number ?? 0) - (a.sub_number ?? 0));

      if (sorted.length === 0) {
        return;
      }

      if (sorted.length === 1) {
        onSelect(buildSearchResult(sorted[0], insurerTypeId));
        return;
      }

      setIntervalRows(sorted);
      setStep('intervals');
    } catch (err) {
      console.error(err);
    }
  };

  const formatPeriod = (from?: string | null, to?: string | null) => {
    const a = from ? convGtJDate(from) : '';
    const b = to ? convGtJDate(to) : '';
    if (a && b) return `${a} - ${b}`;
    if (a) return `${a} -`;
    return '';
  };

  const intervalColumns = useMemo(
    () => [
      {
        title: '保険者番号-枝番',
        key: 'num',
        width: 140,
        render: (_: unknown, r: MedicalAidInsurer) => `${r.insurer_number}-${r.sub_number ?? ''}`,
      },
      {
        title: '保険者名称',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        ellipsis: true,
      },
      {
        title: '保険者種別',
        key: 'type_name',
        width: 120,
        render: (_: unknown, r: MedicalAidInsurer) =>
          r.insurer_type?.name ??
          medicalAidTypesList.find(t => t.id === r.insurer_type_id)?.name ??
          '',
      },
      {
        title: '一部助成',
        key: 'partial',
        width: 90,
        render: (_: unknown, r: MedicalAidInsurer) => (r.is_partial_aid ? 'あり' : '無し'),
      },
      {
        title: '適用期間（開始～終了）',
        key: 'period',
        width: 280,
        render: (_: unknown, r: MedicalAidInsurer) =>
          formatPeriod(r.available_from, r.available_to),
      },
    ],
    [medicalAidTypesList]
  );

  const footerBar = [
    <Fragment key="medical-aid-footer-actions">
      {activeStep === 'filters' && (
        <AppButton
          type="primary"
          style={{
            backgroundColor: colors.secondary,
            borderColor: colors.secondary,
          }}
          icon={<i className="mr-2 fa fa-paper-plane" aria-hidden="true" />}
          onClick={handleApplyClick}
          loading={fetchIntervalsMutation.isPending}
        >
          適用
        </AppButton>
      )}
    </Fragment>,
  ];

  return (
    <AppModal
      isOpen={isOpen}
      onClose={() => void handleModalClose()}
      title="医療助成 補助入力"
      maxWidth="max-w-5xl"
      footer={footerBar}
    >
      <AppSpin spinning={searchInsurersMutation.isPending || fetchIntervalsMutation.isPending}>
        {activeStep === 'filters' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex min-w-[140px] flex-col gap-1">
                <span className="text-sm font-medium">種類</span>
                <AppSelect
                  allowClear={false}
                  value={insurerTypeId}
                  onChange={v => handleInsurerTypeChange(Number(v))}
                  className="w-40"
                  options={medicalAidTypeSelectOptions}
                />
              </div>
              <div className="flex min-w-[140px] flex-col gap-1">
                <span className="text-sm font-medium">都道府県</span>
                <AppSelect
                  allowClear={false}
                  value={prefectureName}
                  onChange={v => handlePrefectureChange(String(v))}
                  className="w-40"
                  options={prefectureSelectOptions}
                />
              </div>
              <div className="flex min-w-[200px] flex-1 flex-col gap-1">
                <span className="text-sm font-medium">公費負担者</span>
                <AppSelect
                  allowClear={false}
                  value={selectedInsurerId}
                  onChange={val => setSelectedInsurerId(val == null ? null : (val as number))}
                  placeholder="公費負担者を選択"
                  className="w-full min-w-[240px]"
                  showSearch={{ optionFilterProp: 'label' }}
                  options={medicalAidInsurerSelectOptions}
                />
              </div>
            </div>
          </div>
        )}

        {activeStep === 'intervals' && (
          <div className="mt-2 space-y-3">
            <p className="text-red-600">{MERGE_WARNING_MESSAGE}</p>
            <AppTable<MedicalAidInsurer>
              rowKey={r => `${r.id}-${r.sub_number}`}
              columns={intervalColumns}
              dataSource={activeIntervalRows}
              pagination={false}
              onRow={record => ({
                onClick: () => applyIntervalRow(record),
                style: { cursor: 'pointer' },
              })}
            />
          </div>
        )}
      </AppSpin>
    </AppModal>
  );
};

export default MedicalAidSearchModal;
